import React, { ReactNode, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { CSVLink } from 'react-csv';
import { useLocale } from 'hooks';
import { Grid } from '@material-ui/core';
import clsx from 'clsx';

import { useStyles } from './DataGrid.styles';
import { DataGridColumnConfig, DataGridProps, SortConfig, SortDirection } from './DataGrid.types';
import { DataGridHeader } from './dataGridHeader/DataGridHeader';
import { DataGridPaginationActions } from './dataGridPaginationActions/DataGridPaginationActions';

import { Button, Loader } from '..';

export const DataGrid = <T extends Record<string, unknown>>({
  columnsConfig,
  data,
  onClick,
  hideCsvButton = false,
  loading = false,
}: DataGridProps<T>) => {
  const classes = useStyles();
  const { formatMessage } = useLocale();
  const [page, setPage] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({ field: null, direction: SortDirection.DESC });
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const sortData = (data: T[]): T[] => {
    if (!sortConfig.field) {
      return data;
    }

    const direction = sortConfig.direction === SortDirection.ASC ? 1 : -1;
    return data.sort((a, b) => {
      if (sortConfig.field) {
        return direction * (a[sortConfig.field] < b[sortConfig.field] ? -1 : 1);
      }

      return 1;
    });
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

  const renderCell = (column: DataGridColumnConfig<T>, data: T): ReactNode => {
    if (column.formatCell) {
      return column.formatCell(data);
    }
    return (data[column.name] as string) ?? '';
  };

  const handleHeaderClick = (field: string) => {
    setSortConfig(config => {
      if (config.field === field) {
        return {
          field,
          direction: config.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC,
        };
      }
      return {
        field,
        direction: SortDirection.ASC,
      };
    });
  };

  const csvHeader = columnsConfig.map(column => ({ label: formatMessage({ id: column.labelId }), key: column.name }));

  const displayContent = () => {
    return (
      <>
        {sortData(data)
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((row, index) => {
            return (
              <TableRow
                hover
                tabIndex={-1}
                key={index}
                data-testid="row"
                onClick={() => onClick && onClick(row)}
                className={clsx(!!onClick && classes.clickable)}
              >
                {columnsConfig.map(column => (
                  <TableCell key={`${column.name}_${index}`} scope="row" className={classes.cell}>
                    {renderCell(column, row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
      </>
    );
  };

  return (
    <>
      {!hideCsvButton && (
        <Grid container justify="flex-end" className={classes.csvButtonContainer}>
          <Grid item xs={3}>
            <CSVLink data={data} headers={csvHeader} className={classes.csvLink} filename="Battery_list">
              <Button className={classes.csvButton}>Download CSV</Button>
            </CSVLink>
          </Grid>
        </Grid>
      )}
      <div className={classes.table}>
        <TableContainer>
          <Table aria-labelledby="tableTitle" aria-label="enhanced table">
            <DataGridHeader<T>
              columnsConfig={columnsConfig}
              onHeaderClick={handleHeaderClick}
              sortConfig={sortConfig}
            />
            <TableBody className={classes.tableBody}>
              {loading ? (
                <div className={classes.loader}>
                  <Loader suppressText />
                </div>
              ) : (
                displayContent()
              )}
              {(emptyRows > 0 || loading) &&
                new Array(loading ? rowsPerPage : emptyRows).fill(0).map((_, index) => (
                  <TableRow data-testid="empty-row" key={`emptyCell_${index}`}>
                    <TableCell className={classes.emptyCell} colSpan={columnsConfig.length} />
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage=""
          className={classes.pagination}
          ActionsComponent={DataGridPaginationActions}
          labelDisplayedRows={({ from, to, count }) => formatMessage({ id: 'data_grid.rows' }, { from, to, count })}
        />
      </div>
    </>
  );
};
